import React, {
  Fragment,
  RefObject,
  useImperativeHandle,
  useRef,
} from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { AVPlaybackStatus, Video } from 'expo-av';
import { Portal } from '@gorhom/portal';
import Animated, {
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { SxProp, useSx } from 'dripsy';

import { VideoPlayer } from '../VideoPlayer';
import {
  FullScreenVideoPlayerView,
  IFullScreenVideoPlayerViewProps,
} from './FullScreenVideoPlayerView';
import { IS_MOBILE } from '@utils';

const containerStyle: SxProp = {
  overflow: 'hidden',
};

export interface IFullScreenVideoPlayerProp
  extends IFullScreenVideoPlayerViewProps {
  videoPlayerRef?: RefObject<Video>;
  fullScreenVideoPlayerRef?: RefObject<Video>;
  isVisibleFullScreenPlayer?: boolean;
}

export const FullScreenVideoPlayer =
  observer<IFullScreenVideoPlayerProp>(
    ({
      keyId,
      videoPlayerRef,
      fullScreenVideoPlayerRef,
      isVisibleFullScreenPlayer = false,
      onToggleFullScreen,
      shouldPlay,
      ...props
    }) => {
      const videoPlayerInnerRef = useRef<Video>(null);
      const fullScreenVideoPlayerInnerRef = useRef<Video>(null);
      const sx = useSx();
      useImperativeHandle(
        videoPlayerRef,
        () => videoPlayerInnerRef.current!,
      );
      useImperativeHandle(
        fullScreenVideoPlayerRef,
        () => fullScreenVideoPlayerInnerRef.current!,
      );

      const store = useLocalObservable(() => ({
        isVisibleFullScreenPlayer,
        playerStatus: null as null | AVPlaybackStatus,

        switchFullScreen(status: AVPlaybackStatus) {
          this.isVisibleFullScreenPlayer =
            !this.isVisibleFullScreenPlayer;
          this.playerStatus = status;
        },
      }));

      const aIsVisibleFullScreenPlayer =
        store.isVisibleFullScreenPlayer;
      const animatedStyle = useAnimatedStyle(
        () => ({
          height: withTiming(
            aIsVisibleFullScreenPlayer ? '100%' : 0,
            {
              duration: 100,
            },
          ),
        }),
        [aIsVisibleFullScreenPlayer],
      );

      function switchFullScreen(playerStatus: AVPlaybackStatus) {
        if (store.isVisibleFullScreenPlayer) {
          videoPlayerInnerRef.current?.setStatusAsync(playerStatus);
          fullScreenVideoPlayerInnerRef.current?.stopAsync();
        } else {
          fullScreenVideoPlayerInnerRef.current?.setStatusAsync(
            playerStatus,
          );
          videoPlayerInnerRef.current?.stopAsync();
        }

        store.switchFullScreen(playerStatus);
        onToggleFullScreen?.(playerStatus);
      }

      // The `!store.isVisibleFullScreenPlayer` condition ensures that the video player
      // doesn't start playing when the user quickly enters fullscreen mode before the video has loaded
      const videoPlayerShouldPlay =
        shouldPlay && !store.isVisibleFullScreenPlayer;
      // The `store.isVisibleFullScreenPlayer` check ensures that the fullscreen video player
      // only starts playing when fullscreen mode is actually active
      const fullScreenVideoPlayerShouldPlay =
        shouldPlay && store.isVisibleFullScreenPlayer;

      return (
        <Fragment>
          <VideoPlayer
            ref={videoPlayerInnerRef}
            onToggleFullScreen={switchFullScreen}
            shouldPlay={videoPlayerShouldPlay}
            {...props}
          />

          {IS_MOBILE && (
            <Portal>
              <Animated.View
                style={[sx(containerStyle), animatedStyle]}
              >
                <FullScreenVideoPlayerView
                  keyId={keyId}
                  ref={fullScreenVideoPlayerInnerRef}
                  onToggleFullScreen={switchFullScreen}
                  isInFullScreen={store.isVisibleFullScreenPlayer}
                  shouldPlay={fullScreenVideoPlayerShouldPlay}
                  {...props}
                />
              </Animated.View>
            </Portal>
          )}
        </Fragment>
      );
    },
  );
